/*
 * Client-side CSV export of the delivery-and-payment table (EMS-10148). Builds
 * straight from the rows already loaded for InvoiceDeliveryTableWithStripePayment,
 * so it respects whatever status/search/archive filters are currently applied —
 * unlike the period ZIP download, which always covers the whole billing period.
 */
import dayjs from 'dayjs'
import { downloadJSONasCSV } from 'helpers/download-file'
import { normalizeAmountTwoDecimals } from 'helpers/utility'
import {
  InvoiceDeliveryItemDto,
  InvoicePaymentAttemptDto,
  InvoicePaymentType,
} from '../../api/dto/invoice-delivery.dto'
import { formatStatusLabel } from './generation/utils'
import {
  NOT_STARTED_STATUS,
  SUCCEEDED_STATUS,
  centsToDollars,
  formatDrawStatusLabel,
  normalizePaymentType,
} from './paymentStatus'

interface PaymentReportRow {
  Company: string
  'Payment Type': string
  'Stripe Customer ID': string
  Location: string
  'Bank Account': string
  'Stripe Account': string
  'Due Date': string
  Amount: string
  'Actual Amount': string
  'Draw Status': string
}

const formatAttemptStatusLabel = (status: string | null | undefined): string =>
  !status || status === NOT_STARTED_STATUS
    ? 'Not Started'
    : formatStatusLabel(status)

/** Blank when the delivery has no due date set. */
const formatDueDate = (dueDate: string | null | undefined): string =>
  dueDate ? dayjs(dueDate).format('MM/DD/YYYY') : ''

/** Blank when the client has no billing profile — nothing to report. */
const formatPaymentTypeLabel = (
  paymentType: string | null | undefined
): string => {
  const normalized = normalizePaymentType(paymentType)
  if (normalized === null) return ''
  return normalized === InvoicePaymentType.WIRE ? 'Wire' : 'Stripe'
}

/** Blank when the attempt has no bank payment method attached yet. */
const formatBankAccountLabel = (
  attempt: InvoicePaymentAttemptDto | undefined
): string => {
  const bankAccount = attempt?.stripePaymentMethods?.[0]?.us_bank_account
  if (!bankAccount?.bank_name) return ''
  return `${bankAccount.bank_name} (** ${bankAccount.last4 ?? ''})`
}

/*
 * Attempt-level Actual Amount is the settled draw only. Failed, pending, and
 * not-started rows still appear (their Draw Status is the signal) but must
 * not contribute a dollar figure — `paymentAmount` on those attempts is a
 * requested/attempted amount, and a missing amount is unknown, not $0.
 */
const formatSettledAttemptAmount = (
  attempt: InvoicePaymentAttemptDto
): string =>
  attempt.paymentStatus === SUCCEEDED_STATUS &&
  typeof attempt.paymentAmount === 'number'
    ? normalizeAmountTwoDecimals(centsToDollars(attempt.paymentAmount))
    : ''

/*
 * One row per company totaling the invoice, followed by one row per draw
 * attempt in its `paymentList` — so a company billed through several Stripe
 * customers (or retried more than once) gets a visible breakdown rather than
 * a single opaque total. When only one distinct Stripe customer is involved,
 * that breakdown would just repeat the same id under a redundant "Total"
 * row — so the company row shows that customer directly instead.
 */
export const buildPaymentReportRows = (
  deliveries: InvoiceDeliveryItemDto[]
): PaymentReportRow[] =>
  deliveries.flatMap((record) => {
    const paymentList = record.paymentDetail?.paymentList ?? []
    const distinctCustomerIds = new Set(
      paymentList
        .map((attempt) => attempt.stripeCustomerId)
        .filter((id): id is string => !!id)
    )

    const amount = normalizeAmountTwoDecimals(record.invoiceMetadata?.totalDue)
    const actualAmount = normalizeAmountTwoDecimals(
      typeof record.paymentDetail?.totalSucceededAmount === 'number'
        ? centsToDollars(record.paymentDetail.totalSucceededAmount)
        : null
    )
    const drawStatus = formatDrawStatusLabel(record)
    const paymentType = formatPaymentTypeLabel(record.paymentType)
    const dueDate = formatDueDate(record.dueDate)

    if (distinctCustomerIds.size === 1) {
      const onlyAttempt = paymentList.find(
        (attempt) => attempt.stripeCustomerId
      )
      return [
        {
          Company: record.clientName ?? '',
          'Payment Type': paymentType,
          'Stripe Customer ID': onlyAttempt?.stripeCustomerId ?? '',
          Location: onlyAttempt?.stripeBillingProfileDescription ?? '',
          'Bank Account': formatBankAccountLabel(onlyAttempt),
          'Stripe Account': onlyAttempt?.stripeAccount ?? '',
          'Due Date': dueDate,
          Amount: amount,
          'Actual Amount': actualAmount,
          'Draw Status': drawStatus,
        },
      ]
    }

    const totalRow: PaymentReportRow = {
      Company: record.clientName ?? '',
      'Payment Type': paymentType,
      'Stripe Customer ID': 'Total',
      Location: '',
      'Bank Account': '',
      'Stripe Account': '',
      'Due Date': dueDate,
      Amount: amount,
      'Actual Amount': actualAmount,
      'Draw Status': drawStatus,
    }

    const attemptRows: PaymentReportRow[] = paymentList.map((attempt) => ({
      Company: '',
      'Payment Type': '',
      'Stripe Customer ID': attempt.stripeCustomerId ?? '',
      Location: attempt.stripeBillingProfileDescription ?? '',
      'Bank Account': formatBankAccountLabel(attempt),
      'Stripe Account': attempt.stripeAccount ?? '',
      'Due Date': '',
      Amount: '',
      'Actual Amount': formatSettledAttemptAmount(attempt),
      'Draw Status': formatAttemptStatusLabel(attempt.paymentStatus),
    }))

    return [totalRow, ...attemptRows]
  })

export const downloadPaymentReportCsv = (
  deliveries: InvoiceDeliveryItemDto[],
  billingPeriodStart: string
): void => {
  const yearMonth = billingPeriodStart.slice(0, 7)
  downloadJSONasCSV(
    buildPaymentReportRows(deliveries),
    `payments-${yearMonth}.csv`
  )
}
